import { EmployeeInline } from "@/components/employee/EmployeeInline.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  useEmployeeJobsPinsSetting,
  useEmployeeJobsPinsSettingSave,
} from "@/queries/use-employee-settings.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiJob,
  getJobEmoji,
  getJobPriorityTitle,
  isJobActive,
  JobPriority,
  JobStatus,
  JobStatusItems,
} from "@/types/recruit/job.types";
import {
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  LinkTo,
  ListItem,
  Stack,
  Table,
  TableCell,
  Tag,
  Text,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface JobsTableProps {
  data?: ApiJob[];
  isLoading: boolean;
  isFetching: boolean;
  onEdit?: (job: ApiJob) => void;
  onDuplicate?: (job: ApiJob) => void;
  showNotFoundError?: boolean;
  notFoundComponent?: ReactNode;
  emptyStateComponent?: ReactNode;
}

export function JobsTable({
  data,
  isLoading,
  isFetching,
  showNotFoundError,
  onEdit,
  onDuplicate,
  notFoundComponent,
  emptyStateComponent,
}: JobsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: jobsPinsSetting } = useEmployeeJobsPinsSetting();
  const jobsPinsSettingSave = useEmployeeJobsPinsSettingSave();
  const pinnedJobIds = useMemo(() => jobsPinsSetting?.job_ids || [], [jobsPinsSetting]);
  const priorityTagVariantByPriority = {
    [JobPriority.low]: "green",
    [JobPriority.medium]: "positive",
    [JobPriority.high]: "danger",
  } as const;

  const renderPriorityLabel = (priority: JobPriority | null) => {
    if (!priority) return null;
    const tagVariant = priorityTagVariantByPriority[priority];

    return (
      <Stack items={"start"}>
        <Tag appearance={"outline"} variant={tagVariant}>
          {getJobPriorityTitle(priority)}
        </Tag>
      </Stack>
    );
  };

  const toggleRecruitPinnedJob = useCallback(
    (jobId: number) => {
      const nextPinnedJobIds = pinnedJobIds.includes(jobId)
        ? pinnedJobIds.filter((id) => id !== jobId)
        : [...pinnedJobIds, jobId];
      jobsPinsSettingSave.mutate({ job_ids: nextPinnedJobIds });
    },
    [jobsPinsSettingSave, pinnedJobIds]
  );

  const getJobStatusTag = (entity: ApiJob) => {
    return (
      <Stack>
        <span>{JobStatusItems().find((x) => x.id == entity.status)?.title}</span>
        {entity.status == JobStatus.archived && <span>{entity.job_archiving_reason?.title}</span>}
      </Stack>
    );
  };
  const { renderRelativeDate } = useFormatter();

  const columns = useMemo<ColumnDef<ApiJob>[]>(() => {
    const inner: ColumnDef<ApiJob>[] = [
      {
        header: t("ats:job"),
        cell: (row) => {
          const job = row.row.original;
          const isPinned = pinnedJobIds.includes(job.id);

          return (
            <div className={"flex items-stretch"}>
              <LinkTo variant={"plain"} to={`/recruitment/candidates/?job_id=${job.id}`}>
                <ListItem
                  emoji={getJobEmoji(job)}
                  title={job.title}
                  titleSuffix={
                    isPinned ? (
                      <span className={"whitespace-nowrap"}>
                        {"\u00A0\u00A0"}
                        <FontAwesomeIcon icon="fa-light fa-thumbtack-angle" />
                      </span>
                    ) : undefined
                  }
                  behavior={"card"}
                />
              </LinkTo>
            </div>
          );
        },
        accessorKey: "title",
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
      },
      {
        header: t("ats:job_priority"),
        cell: (row) => renderPriorityLabel(row.row.original.priority),
        accessorKey: "priority",
        ...ColumnSizes.status,
      },
      {
        header: t("common:created"),
        cell: (row) => <Text>{renderRelativeDate(row.row.original.datetime_created_at)}</Text>,
        accessorKey: "created_at",
        ...ColumnSizes.status,
      },
      {
        header: t("ats:candidates"),
        cell: (row) => (
          <TableCell variant={"secondary"}>{row.row.original.candidates_count}</TableCell>
        ),
        accessorKey: "candidates_count",
        ...ColumnSizes.status,
      },
      {
        header: t("core:department"),
        cell: (row) => (
          <TableCell variant={"secondary"}>{row.row.original.department?.title || "-"}</TableCell>
        ),
        accessorKey: "department",
        ...ColumnSizes.standard,
      },
      {
        header: t("core:division"),
        cell: (row) => (
          <TableCell variant={"secondary"}>{row.row.original.division?.title || "-"}</TableCell>
        ),
        accessorKey: "division",
        ...ColumnSizes.standard,
      },
      {
        header: t("ats:stakeholders"),
        cell: (row) => <EmployeeInline employees={row.row.original.stakeholders} />,
        accessorKey: "manager",
        ...ColumnSizes.standard,
        meta: { disableRowClick: true },
      },
      {
        header: t("ats:recruiters"),
        cell: (row) => <EmployeeInline employees={row.row.original.recruiters} />,
        accessorKey: "recruiter",
        ...ColumnSizes.standard,
        meta: { disableRowClick: true },
      },

      {
        header: t("common:status"),
        cell: (row) => <Stack items={"start"}>{getJobStatusTag(row.row.original)}</Stack>,
        accessorKey: "status",
        ...ColumnSizes.status,
      },
    ];

    return inner;
  }, [pinnedJobIds, renderRelativeDate, t]);

  const renderRowAction = useCallback(
    (row: Row<ApiJob>) => {
      if (!onEdit) return null;
      const job = row.original;
      const canPinJob = isJobActive(job.status);
      const isPinned = pinnedJobIds.includes(job.id);

      return (
        <DropdownMenu>
          <DropdownMenuItem
            title={t("common:edit")}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
            onClick={() => onEdit(job)}
          />
          {onDuplicate && (
            <DropdownMenuItem
              title={t("ats:duplicate_job")}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-copy"} />}
              onClick={() => onDuplicate(job)}
            />
          )}
          {canPinJob && (
            <DropdownMenuItem
              title={isPinned ? t("common:unpin") : t("common:pin")}
              leftIcon={
                <FontAwesomeIcon
                  icon={`fa-light ${isPinned ? "fa-thumbtack-angle-slash" : "fa-thumbtack-angle"}`}
                />
              }
              onClick={() => toggleRecruitPinnedJob(job.id)}
            />
          )}
        </DropdownMenu>
      );
    },
    [onEdit, onDuplicate, pinnedJobIds, t, toggleRecruitPinnedJob]
  );

  return (
    <Table
      renderRowAction={renderRowAction}
      onRowClick={(row) => navigate(`/recruitment/candidates/?job_id=${row.original.id}`)}
      data={data || []}
      columns={columns}
      showSkeleton={isLoading}
      isFetching={isFetching}
      stickyHeader
      showNotFoundError={showNotFoundError}
      notFoundComponent={notFoundComponent}
      emptyStateComponent={emptyStateComponent}
    />
  );
}
